/*
 * Combined Flavor and Size Selector Logic for Replo Product Forms
 * Handles image filtering based on both flavor and size selections
 */

class ReploProductForm {
  constructor() {
    this.selectorToggle = document.querySelector('[data-selector-toggle]');
    this.selectorContent = document.querySelector('[data-selector-content]');
    this.selectorToggleIcon = this.selectorToggle?.querySelector('svg');
    this.flavorInput = document.querySelector("#flavor-value");
    this.thumbnailSlider = document.querySelector('.thumbs-splide');
    this.mainSlider = document.querySelector('.main-splide');
    
    // Get product data from JSON script tag
    this.productData = this.getProductData();
    this.productVariants = this.productData.variants || [];
    
    // Store initial slides for filtering
    this.initialMainSlides = this.mainSlider ? [...this.mainSlider.splide.Components.Elements.slides] : [];
    this.initialThumbSlides = this.thumbnailSlider ? [...this.thumbnailSlider.splide.Components.Elements.slides] : [];
    
    // Current selections
    this.currentFlavor = '';
    this.currentSize = '';
    
    this.init();
  }

  getProductData() {
    try {
      const scriptElement = document.getElementById('replo-product-data');
      return scriptElement ? JSON.parse(scriptElement.textContent) : {};
    } catch (error) {
      console.error('Failed to parse product data:', error);
      return {};
    }
  }

  init() {
    if (!this.selectorToggle || !this.selectorContent) return;
    
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeSelections();
      this.setupEventListeners();
      this.handleUrlVariant();
    });
  }

  initializeSelections() {
    // Check if we have a URL variant first
    const urlVariantId = this.getUrlVariantId();
    
    if (urlVariantId) {
      // If URL has variant, sync UI to match it
      this.syncUIToVariant(urlVariantId);
    } else {
      // Otherwise use default selections
      // Get initial flavor from first option
      const firstFlavorButton = this.selectorContent.querySelector('label');
      if (firstFlavorButton) {
        this.currentFlavor = firstFlavorButton.dataset.flavor;
      }

      // Get initial size selection
      const sizeInput = document.querySelector('input[name*="option-size"]:checked, input[name*="option-Size"]:checked');
      if (sizeInput) {
        this.currentSize = sizeInput.value;
      }
    }

    this.filterAndUpdateSliders();
  }

  setupEventListeners() {
    // Flavor selection listeners
    this.selectorContent.querySelectorAll('label').forEach((button) => {
      button.addEventListener('click', () => {
        this.handleFlavorChange(button);
      });
    });

    // Selector toggle listener
    this.selectorToggle.addEventListener('click', () => {
      this.toggleDropdown();
    });

    // Size option listeners
    this.setupSizeListeners();
  }

  setupSizeListeners() {
    // Listen for size changes on radio buttons
    const sizeInputs = document.querySelectorAll('input[name*="option-size"], input[name*="option-Size"]');
    sizeInputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        this.handleSizeChange(event.target.value);
      });
    });
  }

  handleFlavorChange(button) {
    const currId = document.querySelector('[name="id"]');
    
    // Update UI
    this.selectorToggle.querySelector('[data-flavor-content]').innerHTML = button.innerHTML;
    this.selectorToggle.dataset.selectedOption = button.dataset.flavor;
    this.flavorInput.value = button.dataset.flavor;
    
    // Update current flavor
    this.currentFlavor = button.dataset.flavor;
    
    // Find the correct variant based on new flavor + current size combination
    const currentVariantId = this.findVariantByOptions();
    if (currentVariantId) {
      this.handleVariantChange(currentVariantId);
    }
    
    // Trigger product option change
    onChangeProductOption(this.flavorInput);
    
    // Filter images based on new selection
    this.filterAndUpdateSliders();
    
    // Close dropdown
    this.selectorToggle.click();
    
    // Handle variant image selection
    this.handleVariantImageSelection(currId);
  }

  handleSizeChange(newSize) {
    this.currentSize = newSize;
    this.filterAndUpdateSliders();
    
    // Find the correct variant based on current flavor and size combination
    const currentVariantId = this.findVariantByOptions();
    if (currentVariantId) {
      this.handleVariantChange(currentVariantId);
    }
  }

  findVariantByOptions() {
    // Get all current option selections
    const selectedOptions = [];
    
    // Add flavor
    if (this.currentFlavor) {
      selectedOptions.push(this.currentFlavor);
    }
    
    // Add size
    if (this.currentSize) {
      selectedOptions.push(this.currentSize);
    }
    
    // Add any other options
    const form = document.querySelector('form');
    if (form) {
      form.querySelectorAll('.product-option').forEach(elem => {
        if (elem.type === 'radio') {
          if (elem.checked && !selectedOptions.includes(elem.value)) {
            selectedOptions.push(elem.value);
          }
        } else if (elem.type === 'select-one') {
          if (!selectedOptions.includes(elem.value)) {
            selectedOptions.push(elem.value);
          }
        }
      });
    }
    
    // Find matching variant
    const matchingVariant = this.productVariants.find(variant =>
      JSON.stringify(variant.options.sort()) === JSON.stringify(selectedOptions.sort())
    );
    
    return matchingVariant ? matchingVariant.id.toString() : null;
  }

  handleVariantChange(variantId) {
    const variantIdInputs = document.querySelectorAll('[name="id"]');
    
    if (variantIdInputs.length > 0 && variantId) {
      // Update variant ID input
      variantIdInputs.forEach(input => {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      // Find the selected variant for Skio
      const selectedVariant = this.productVariants.find(variant => 
        variant.id.toString() === variantId
      );
      
      // Dispatch custom event for Skio component
      if (selectedVariant) {
        const skioVariantChangeEvent = new CustomEvent('skio:variant-change', {
          detail: { 
            selectedVariant: selectedVariant,
            form: variantIdInputs[0].closest('form'),
            variantInput: variantIdInputs[0]
          },
          bubbles: true
        });
        document.dispatchEvent(skioVariantChangeEvent);
      }
    }
  }

  filterAndUpdateSliders() {
    if (!this.mainSlider || !this.thumbnailSlider) return;

    const filteredFlavors = [];
    const filteredThumbs = [];

    for (let i = 0; i < this.initialMainSlides.length; i++) {
      const slide = this.initialMainSlides[i];
      const thumbSlide = this.initialThumbSlides[i];
      const imgElement = slide.querySelector('.img-fluid');
      
      if (!imgElement) continue;
      
      const altText = imgElement.alt;
      const shouldShow = this.shouldShowImage(altText);
      
      if (shouldShow) {
        filteredFlavors.push(slide);
        filteredThumbs.push(thumbSlide);
      }
    }

    // Update sliders
    this.thumbnailSlider.splide.Components.Slides.remove('*');
    this.mainSlider.splide.Components.Slides.remove('*');
    this.thumbnailSlider.splide.Components.Slides.add(filteredThumbs);
    this.mainSlider.splide.Components.Slides.add(filteredFlavors);
  }

  shouldShowImage(altText) {
    // Get product title from JSON data
    const productTitle = this.productData.title || '';
    
    // Always show product title images (exact match or base product images)
    if (altText === productTitle) {
      return true;
    }

    if (!this.currentFlavor) {
      return false;
    }
    
    const flavorMatch = altText.toLowerCase().startsWith(this.currentFlavor.toLowerCase());
    
    if (!flavorMatch) {
      return false;
    }

    // Standard flavor handling with size variants
    // If no size is selected, show all flavor images
    if (!this.currentSize) {
      return true;
    }

    // Extract size from current size selection
    const sizeNumber = this.extractSizeNumber(this.currentSize);
    
    // Check size-specific rules in alt text
    if (altText.toLowerCase().includes(' 30')) {
      return sizeNumber === '30';
    } else if (altText.toLowerCase().includes(' 90')) {
      return sizeNumber === '90';
    }
    
    // If no size specified in alt text, show for all sizes (flavor-only images)
    return true;
  }

  extractSizeNumber(sizeText) {
    // Extract number from size text (e.g., "30 Servings" -> "30")
    const match = sizeText.match(/\d+/);
    return match ? match[0] : '';
  }

  handleVariantImageSelection(currId) {
    if (!currId || !this.productVariants.length) return;

    const currVariant = this.productVariants.find(variant => 
      variant.id.toString() === currId.value
    );

    if (currVariant?.featured_image?.src?.includes('files')) {
      const currVariantImage = currVariant.featured_image.src.split('/files/')[1].split('.')[0];
      
      this.thumbnailSlider.splide.Components.Slides.forEach((slide) => {
        const slideImage = slide.slide.querySelector('img');
        if (slideImage?.src?.includes(currVariantImage)) {
          slide.slide.click();
        }
      });
    }
  }

  getUrlVariantId() {
    // Check URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    // Also check hash-based params
    if (!variantId && window.location.href.includes('&variant=')) {
      const match = window.location.href.match(/[?&]variant=(\d+)/);
      return match ? match[1] : null;
    }
    
    return variantId;
  }

  syncUIToVariant(variantId) {
    // Find the variant in our product data
    const variant = this.productVariants.find(v => v.id.toString() === variantId);
    
    if (!variant) {
      console.warn('[Replo] Variant not found:', variantId);
      return;
    }

    console.log('[Replo] Syncing UI to URL variant:', variant);

    // Extract flavor and size from variant options
    // Variants have options array matching the product's option order
    const options = variant.options;
    
    // Identify which option is flavor and which is size
    // Common patterns: option1 is often flavor, option2 is size
    let flavorValue = null;
    let sizeValue = null;

    // Try to match options intelligently
    for (const optionValue of options) {
      if (!optionValue) continue;
      
      // Size patterns (servings/bags)
      if (optionValue.toLowerCase().includes('serving') || 
          optionValue.toLowerCase().includes('bag') ||
          /^\d+\s*(servings?|bags?)$/i.test(optionValue)) {
        sizeValue = optionValue;
      } else {
        // Assume it's a flavor
        flavorValue = optionValue;
      }
    }

    // Update internal state
    if (flavorValue) {
      this.currentFlavor = flavorValue;
      
      // Update flavor UI
      const flavorButton = this.selectorContent.querySelector(`label[data-flavor="${flavorValue}"]`);
      if (flavorButton) {
        this.selectorToggle.querySelector('[data-flavor-content]').innerHTML = flavorButton.innerHTML;
        this.selectorToggle.dataset.selectedOption = flavorValue;
      }
      
      if (this.flavorInput) {
        this.flavorInput.value = flavorValue;
      }
    }

    if (sizeValue) {
      this.currentSize = sizeValue;
      
      // Update size UI - check the correct radio button
      const sizeInputs = document.querySelectorAll('input[name*="option-size"], input[name*="option-Size"]');
      sizeInputs.forEach(input => {
        if (input.value === sizeValue) {
          input.checked = true;
        }
      });
    }

    // Dispatch variant change event for Skio
    this.handleVariantChange(variantId);
  }

  handleUrlVariant() {
    const urlVariantId = this.getUrlVariantId();
    if (urlVariantId) {
      this.syncUIToVariant(urlVariantId);
    }
  }

  toggleDropdown() {
    if (this.selectorContent.style.gridTemplateRows === '1fr') {
      this.selectorContent.style.gridTemplateRows = '0fr';
      this.selectorContent.style.opacity = 0;
      this.selectorToggleIcon.style.transform = 'rotate(0deg)';
    } else {
      this.selectorContent.style.gridTemplateRows = '1fr';
      this.selectorContent.style.opacity = 1;
      this.selectorToggleIcon.style.transform = 'rotate(-180deg)';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ReploProductForm();
  });
} else {
  new ReploProductForm();
}